import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  CalendarClock,
  Eye,
  EyeOff,
  GripVertical,
  ImagePlus,
  Link2,
  Plus,
  Save,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LessonBlockEditor } from "@/components/LessonBlockEditor";
import { CourseExtrasEditor } from "@/components/CourseExtrasEditor";
import { LargeVideoUploader } from "@/components/LargeVideoUploader";
import { LessonAdvancedSettings } from "@/components/LessonAdvancedSettings";
import { LessonCustomFormEditor } from "@/components/LessonCustomFormEditor";
import { LessonVisibilityEditor } from "@/components/LessonVisibilityEditor";
import { CertificateAdvancedEditor } from "@/components/CertificateAdvancedEditor";
import { createId } from "@/lib/id";

const blank = {
  title: "",
  subtitle: "",
  description: "",
  cover_url: "/marsem-course-hero.png",
  status: "draft",
  has_curator: false,
  curator_id: null,
  show_module_numbers: true,
  show_lesson_numbers: true,
  show_course_duration: true,
  homework_enabled: true,
  access_type: "lifetime",
  access_duration_days: 90,
  access_valid_until: "",
  modules: [],
  bonuses: [],
  whatsapp_groups: [],
  certificate: {
    enabled: false,
    template_type: "default",
    template_file_id: null,
    identity_required: true,
    text_color: "#f5e6c8",
    name_y: 50,
    course_y: 64,
    date_y: 76,
  },
};
const makeLesson = () => ({
  id: createId(),
  item_type: "lesson",
  title: "Nowa lekcja",
  number_label: "",
  duration: "10 min",
  video_type: "youtube",
  video_url: "",
  video_poster: "",
  content: "",
  content_blocks: [],
  access_rule: "always",
  access_dependency_id: null,
  access_dependency_status: "completed",
  access_date: "",
  access_timer_hours: 24,
  homework_enabled: false,
  assignment_type: "text",
  review_mode: "curator",
  review_timer_hours: 24,
  passing_score: 70,
  quiz_questions: [],
  custom_buttons: [],
  custom_form: {
    enabled: false,
    id: createId(),
    title: "Formularz",
    description: "",
    submit_label: "Wyślij",
    fields: [],
    google_sheets_webhook_url: "",
    notification_bot_id: "",
  },
});
const formatDuration = (seconds) => {
  const minutes = Math.ceil((seconds || 0) / 60);
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours} godz. ${minutes % 60} min` : `${minutes} min`;
};

export default function CourseEditorV2() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(blank);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(courseId ? "program" : "settings");
  const [readingDuration, setReadingDuration] = useState(false);
  const [bots, setBots] = useState([]);
  useEffect(() => { api.get("/admin/bots").then(({ data }) => setBots(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (courseId)
      api.get(`/courses/${courseId}`).then(({ data }) => {
        setCourse({
          ...data,
          homework_enabled: data.homework_enabled !== false,
          show_module_numbers: data.show_module_numbers !== false,
          show_lesson_numbers: data.show_lesson_numbers !== false,
          show_course_duration: data.show_course_duration !== false,
          access_type: data.access_type || "lifetime",
          access_duration_days: data.access_duration_days || 90,
          access_valid_until: data.access_valid_until || "",
          bonuses: data.bonuses || [],
          whatsapp_groups: data.whatsapp_groups || [],
          certificate: data.certificate || blank.certificate,
        });
        setSelected(data.modules?.[0]?.lessons?.[0]?.id || null);
      });
  }, [courseId]);
  const addModule = () =>
    setCourse((c) => ({
      ...c,
      modules: [
        ...(c.modules || []),
        {
          id: createId(),
          title: `Nowy moduł ${(c.modules || []).length + 1}`,
          number_label: "",
          lessons: [],
        },
      ],
    }));
  const addLesson = (moduleId) => {
    const lesson = makeLesson();
    setCourse((c) => ({
      ...c,
      modules: c.modules.map((m) =>
        m.id === moduleId ? { ...m, lessons: [...m.lessons, lesson] } : m,
      ),
    }));
    setSelected(lesson.id);
  };
  const deleteLesson = (moduleId, lessonId) => {
    if (!window.confirm("Usunąć tę lekcję? Tej operacji nie można cofnąć."))
      return;
    setCourse((c) => {
      const modules = c.modules.map((m) =>
        m.id === moduleId
          ? { ...m, lessons: m.lessons.filter((l) => l.id !== lessonId) }
          : m,
      );
      if (selected === lessonId)
        setSelected(modules.flatMap((m) => m.lessons)[0]?.id || null);
      return { ...c, modules };
    });
    toast.success("Lekcja usunięta — zapisz kurs, aby zatwierdzić zmianę");
  };
  const lesson = course.modules
    .flatMap((m) => m.lessons)
    .find((l) => l.id === selected);
  const updateLesson = (field, value) =>
    setCourse((c) => ({
      ...c,
      modules: c.modules.map((m) => ({
        ...m,
        lessons: m.lessons.map((l) =>
          l.id === selected ? { ...l, [field]: value } : l,
        ),
      })),
    }));
  const updateVideoUrl = (value) =>
    setCourse((current) => ({
      ...current,
      modules: current.modules.map((module) => ({
        ...module,
        lessons: module.lessons.map((item) =>
          item.id === selected
            ? {
                ...item,
                video_url: value,
                duration_seconds: 0,
                duration: "",
                duration_source_url: null,
              }
            : item,
        ),
      })),
    }));
  const videoReady = useCallback(
    (fileId, durationSeconds) => {
      setCourse((c) => ({
        ...c,
        modules: c.modules.map((m) => ({
          ...m,
          lessons: m.lessons.map((l) =>
            l.id === selected
              ? {
                  ...l,
                  video_url: `/api/files/${fileId}`,
                  video_type: "upload",
                  duration_seconds: durationSeconds || l.duration_seconds,
                  duration: durationSeconds
                    ? formatDuration(durationSeconds)
                    : l.duration,
                }
              : l,
          ),
        })),
      }));
    },
    [selected],
  );
  useEffect(() => {
    if (
      !lesson?.video_url ||
      lesson.duration_seconds ||
      lesson.video_type === "upload" ||
      !/^https?:/i.test(lesson.video_url)
    )
      return;
    const lessonId = lesson.id;
    const timer = setTimeout(() => {
      setReadingDuration(true);
      api
        .get("/videos/metadata", { params: { url: lesson.video_url } })
        .then(({ data }) =>
          setCourse((current) => ({
            ...current,
            modules: current.modules.map((module) => ({
              ...module,
              lessons: module.lessons.map((item) =>
                item.id === lessonId
                  ? {
                      ...item,
                      duration_seconds: data.duration_seconds,
                      duration: data.duration,
                      duration_source_url: lesson.video_url,
                    }
                  : item,
              ),
            })),
          })),
        )
        .catch(() =>
          toast.error("Nie udało się automatycznie odczytać czasu wideo"),
        )
        .finally(() => setReadingDuration(false));
    }, 700);
    return () => clearTimeout(timer);
  }, [
    lesson?.duration_seconds,
    lesson?.id,
    lesson?.video_type,
    lesson?.video_url,
  ]);
  const addQuestion = () =>
    updateLesson("quiz_questions", [
      ...(lesson.quiz_questions || []),
      {
        id: createId(),
        question: "Nowe pytanie",
        options: ["Odpowiedź A", "Odpowiedź B", "Odpowiedź C"],
        correct_index: 0,
      },
    ]);
  const updateQuestion = (id, patch) =>
    updateLesson(
      "quiz_questions",
      (lesson.quiz_questions || []).map((q) =>
        q.id === id ? { ...q, ...patch } : q,
      ),
    );
  const addButton = () =>
    updateLesson("custom_buttons", [
      ...(lesson.custom_buttons || []),
      {
        id: createId(),
        label: "Nowy przycisk",
        url: "https://",
        style: "gold",
        icon: "external",
        size: "medium",
        appearance: { mode: "always" },
        new_tab: true,
      },
    ]);
  const updateButton = (id, patch) => updateLesson(
    "custom_buttons",
    (lesson.custom_buttons || []).map((button) => button.id === id ? { ...button, ...patch } : button),
  );
  const moveButton = (id, direction) => {
    const buttons = [...(lesson.custom_buttons || [])];
    const from = buttons.findIndex((button) => button.id === id);
    const to = Math.max(0, Math.min(buttons.length - 1, from + direction));
    if (from < 0 || from === to) return;
    const [button] = buttons.splice(from, 1);
    buttons.splice(to, 0, button);
    updateLesson("custom_buttons", buttons);
  };
  const save = async (statusOverride = null) => {
    if (!course.title) {
      toast.error("Podaj nazwę kursu");
      return null;
    }
    setSaving(true);
    try {
      const nextStatus = statusOverride || course.status;
      const payload = { ...course, status: nextStatus };
      [
        "id",
        "created_at",
        "updated_at",
        "progress",
        "enrollment",
        "locked_lessons",
        "total_duration",
        "total_duration_seconds",
      ].forEach((key) => delete payload[key]);
      const { data } = courseId
        ? await api.put(`/courses/${courseId}`, payload)
        : await api.post("/courses", payload);
      setCourse((current) => ({ ...current, ...data, status: nextStatus }));
      toast.success(
        nextStatus !== course.status
          ? nextStatus === "published"
            ? "Kurs opublikowany"
            : "Publikacja wyłączona"
          : "Zmiany zapisane",
      );
      if (!courseId) navigate(`/admin/kursy/${data.id}`);
      return data;
    } catch {
      toast.error("Nie udało się zapisać");
      return null;
    } finally {
      setSaving(false);
    }
  };
  const uploadCover = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/files", form);
      setCourse((current) => ({
        ...current,
        cover_url: `/api/files/${data.id}`,
      }));
      toast.success("Okładka przesłana");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Nie udało się przesłać okładki",
      );
    } finally {
      event.target.value = "";
    }
  };
  const coverPreview = course.cover_url?.startsWith("/api/files/")
    ? `${process.env.REACT_APP_BACKEND_URL}${course.cover_url}?auth=${localStorage.getItem("marsem_token")}`
    : course.cover_url;
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/files", form);
      updateLesson("video_url", `/api/files/${data.id}`);
      updateLesson("video_type", "upload");
      toast.success("Film przesłany");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Błąd przesyłania");
    }
  };
  const totalDurationSeconds = course.modules
    .flatMap((module) => module.lessons)
    .reduce((sum, item) => sum + Number(item.duration_seconds || 0), 0);
  const openStep = async (next) => {
    if (next !== "settings" && !courseId) {
      const created = await save();
      if (!created) return;
    }
    setStep(next);
  };
  return (
    <div className="editor-v2" data-testid="course-editor-page">
      <header className="editor-v2-header">
        <button
          onClick={() => navigate("/kursy")}
          data-testid="editor-back-button"
        >
          <ArrowLeft />
        </button>
        <div>
          <small>MARSEM CREATOR</small>
          <strong>{course.title || "Nowy kurs"}</strong>
        </div>
        <div>
          <button
            onClick={() => save()}
            disabled={saving}
            data-testid="save-draft-button"
          >
            <Save /> Zapisz
          </button>
          <button
            className="gold-button"
            onClick={() =>
              save(course.status === "published" ? "draft" : "published")
            }
            disabled={saving}
            data-testid="publish-course-button"
          >
            {course.status === "published" ? (
              <>
                <EyeOff /> Wyłącz publikację
              </>
            ) : (
              <>
                <Eye /> Opublikuj
              </>
            )}
          </button>
        </div>
      </header>
      <nav className="course-builder-steps" aria-label="Etapy tworzenia kursu">
        <button
          className={step === "settings" ? "active" : ""}
          onClick={() => openStep("settings")}
        >
          <b>1</b>
          <span>
            Ustawienia kursu<small>Nazwa, opis i okładka</small>
          </span>
        </button>
        <button
          className={step === "program" ? "active" : ""}
          onClick={() => openStep("program")}
        >
          <b>2</b>
          <span>
            Program<small>Moduły, lekcje i wideo</small>
          </span>
        </button>
        <button
          className={step === "extras" ? "active" : ""}
          onClick={() => openStep("extras")}
        >
          <b>3</b>
          <span>
            Dodatki<small>Bonusy i certyfikat</small>
          </span>
        </button>
      </nav>
      {step === "settings" && (
        <main
          className="course-settings-step"
          data-testid="course-settings-step"
        >
          <div className="settings-step-heading">
            <span>KROK 1</span>
            <h1>Podstawowe ustawienia kursu</h1>
            <p>
              Najpierw przygotuj kartę kursu. Program, moduły i lekcje dodasz w
              kolejnym kroku.
            </p>
          </div>
          <section className="course-settings-card">
            <div>
              <label>
                NAZWA KURSU
                <input
                  value={course.title}
                  onChange={(e) =>
                    setCourse({ ...course, title: e.target.value })
                  }
                  data-testid="course-title-input"
                />
              </label>
              <label>
                PODTYTUŁ
                <textarea
                  value={course.subtitle}
                  onChange={(e) =>
                    setCourse({ ...course, subtitle: e.target.value })
                  }
                  data-testid="course-subtitle-input"
                />
              </label>
              <label>
                OPIS KURSU
                <textarea
                  value={course.description}
                  onChange={(e) =>
                    setCourse({ ...course, description: e.target.value })
                  }
                  data-testid="course-description-input"
                />
              </label>
              <div className="course-duration-summary">
                <span>ŁĄCZNY CZAS PROGRAMU</span>
                <strong>{formatDuration(totalDurationSeconds)}</strong>
                <small>
                  Aktualizuje się automatycznie po dodaniu wideo do lekcji.
                </small>
              </div>
              <label className="switch-row course-duration-visibility">
                <span>POKAZUJ UCZNIOM ŁĄCZNY CZAS KURSU<small>Czas nadal jest obliczany i zapisywany, ale można ukryć go w interfejsie ucznia.</small></span>
                <input type="checkbox" checked={course.show_course_duration !== false} onChange={(e) => setCourse({ ...course, show_course_duration: e.target.checked })} data-testid="course-duration-visibility-checkbox" />
              </label>
              <div className="access-duration-settings" data-testid="course-access-settings">
                <div className="access-settings-heading"><CalendarClock /><div><span>OKRES DOSTĘPU</span><small>Dotyczy nowych dostępów przyznanych po zapisaniu ustawień.</small></div></div>
                <label>
                  SPOSÓB NALICZANIA
                  <select value={course.access_type || "lifetime"} onChange={(e) => setCourse({ ...course, access_type: e.target.value })} data-testid="course-access-type">
                    <option value="lifetime">Dostęp bezterminowy</option>
                    <option value="days">Określona liczba dni od zakupu</option>
                    <option value="until">Dostęp do wybranej daty</option>
                  </select>
                </label>
                {course.access_type === "days" ? <label>
                  LICZBA DNI DOSTĘPU
                  <input type="number" min="1" max="36500" value={course.access_duration_days || 90} onChange={(e) => setCourse({ ...course, access_duration_days: Math.max(1, Number(e.target.value) || 1) })} data-testid="course-access-days" />
                  <small>Przykład: 90 dni od momentu zakupu lub ręcznego przyznania dostępu.</small>
                </label> : null}
                {course.access_type === "until" ? <label>
                  DOSTĘP WAŻNY DO
                  <input type="date" value={course.access_valid_until || ""} onChange={(e) => setCourse({ ...course, access_valid_until: e.target.value })} data-testid="course-access-until" />
                  <small>Uczestnik ma dostęp do końca wskazanego dnia czasu polskiego.</small>
                </label> : null}
                {course.access_type === "lifetime" ? <p>Dostęp nie wygaśnie automatycznie. Nadal można go wyłączyć ręcznie lub przez zwrot płatności.</p> : null}
              </div>
              <div className="numbering-settings">
                <span>NUMERACJA</span>
                <label className="switch-row">
                  NUMERY MODUŁÓW
                  <input
                    type="checkbox"
                    checked={course.show_module_numbers !== false}
                    onChange={(e) =>
                      setCourse({
                        ...course,
                        show_module_numbers: e.target.checked,
                      })
                    }
                    data-testid="module-numbering-checkbox"
                  />
                </label>
                <label className="switch-row">
                  NUMERY LEKCJI
                  <input
                    type="checkbox"
                    checked={course.show_lesson_numbers !== false}
                    onChange={(e) =>
                      setCourse({
                        ...course,
                        show_lesson_numbers: e.target.checked,
                      })
                    }
                    data-testid="lesson-numbering-checkbox"
                  />
                </label>
              </div>
              <label className="switch-row">
                WSPARCIE KURATORA
                <input
                  type="checkbox"
                  checked={course.has_curator}
                  onChange={(e) =>
                    setCourse({ ...course, has_curator: e.target.checked })
                  }
                  data-testid="course-curator-checkbox"
                />
              </label>
              <label className="switch-row">
                ZADANIA DOMOWE W KURSIE
                <input
                  type="checkbox"
                  checked={course.homework_enabled !== false}
                  onChange={(e) =>
                    setCourse({
                      ...course,
                      homework_enabled: e.target.checked,
                    })
                  }
                  data-testid="course-homework-checkbox"
                />
              </label>
            </div>
            <div>
              <div className="cover-upload">
                <span>OKŁADKA</span>
                <div className="cover-upload-preview">
                  <img
                    src={coverPreview || "/marsem-course-hero.png"}
                    alt="Podgląd okładki"
                  />
                  <label>
                    <ImagePlus /> Wgraj dowolne zdjęcie
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={uploadCover}
                      data-testid="course-cover-upload"
                    />
                  </label>
                </div>
                <input
                  value={course.cover_url || ""}
                  onChange={(e) =>
                    setCourse({ ...course, cover_url: e.target.value })
                  }
                  placeholder="lub wklej URL obrazu"
                  data-testid="course-cover-input"
                />
              </div>
              <label className="switch-row">
                KURS OPUBLIKOWANY
                <input
                  type="checkbox"
                  checked={course.status === "published"}
                  onChange={(e) =>
                    setCourse({
                      ...course,
                      status: e.target.checked ? "published" : "draft",
                    })
                  }
                  data-testid="course-status-checkbox"
                />
              </label>
            </div>
          </section>
          <button
            className="gold-button settings-next"
            onClick={() => openStep("program")}
          >
            ZAPISZ I PRZEJDŹ DO PROGRAMU <ArrowLeft className="next-arrow" />
          </button>
        </main>
      )}
      {step === "program" && (
        <div className="editor-v2-layout program-only">
          <aside className="structure-pane">
            <span className="pane-label">STRUKTURA PROGRAMU</span>
            {course.modules.map((module, mi) => (
              <section className="structure-module" key={module.id}>
                <header>
                  {course.show_module_numbers !== false && (
                    <input
                      className="module-number-input"
                      aria-label="Numer modułu"
                      value={module.number_label || ""}
                      onChange={(e) =>
                        setCourse((c) => ({
                          ...c,
                          modules: c.modules.map((m) =>
                            m.id === module.id
                              ? { ...m, number_label: e.target.value }
                              : m,
                          ),
                        }))
                      }
                      placeholder={String(mi + 1)}
                      data-testid={`module-number-${module.id}`}
                    />
                  )}
                  <GripVertical />
                  <input
                    value={module.title}
                    onChange={(e) =>
                      setCourse((c) => ({
                        ...c,
                        modules: c.modules.map((m) =>
                          m.id === module.id
                            ? { ...m, title: e.target.value }
                            : m,
                        ),
                      }))
                    }
                    data-testid={`module-title-${module.id}`}
                  />
                  <button
                    aria-label="Usuń moduł"
                    onClick={() =>
                      setCourse((c) => ({
                        ...c,
                        modules: c.modules.filter((m) => m.id !== module.id),
                      }))
                    }
                    data-testid={`delete-module-${module.id}`}
                  >
                    <Trash2 />
                  </button>
                </header>
                {module.lessons.map((item, li) => (
                  <div
                    className={`structure-lesson-row ${selected === item.id ? "active" : ""}`}
                    key={item.id}
                  >
                    <button
                      className="lesson-select"
                      onClick={() => setSelected(item.id)}
                      data-testid={`edit-lesson-${item.id}`}
                    >
                      {course.show_lesson_numbers !== false && (
                        <span>
                          {item.number_label ||
                            `${module.number_label || mi + 1}.${li + 1}`}
                        </span>
                      )}
                      <div>
                        <strong>{item.title}</strong>
                        <small>
                          {item.assignment_type === "quiz"
                            ? "Test"
                            : item.homework_enabled
                              ? "Zadanie"
                              : "Lekcja"}
                        </small>
                      </div>
                    </button>
                    <button
                      className="delete-lesson"
                      aria-label={`Usuń lekcję ${item.title}`}
                      onClick={() => deleteLesson(module.id, item.id)}
                      data-testid={`delete-lesson-${item.id}`}
                    >
                      <Trash2 />
                    </button>
                  </div>
                ))}
                <button
                  className="ghost-add"
                  onClick={() => addLesson(module.id)}
                  data-testid={`add-lesson-${module.id}`}
                >
                  <Plus /> Dodaj lekcję
                </button>
              </section>
            ))}
            <button
              className="glass-add"
              onClick={addModule}
              data-testid="add-module-button"
            >
              <Plus /> DODAJ MODUŁ
            </button>
          </aside>
          <main className="editor-workspace">
            {!lesson ? (
              <div className="editor-welcome" data-testid="editor-empty-state">
                <Sparkles />
                <h2>Zbuduj nowoczesny program</h2>
                <p>Dodaj moduł, lekcję i skonfiguruj ścieżkę ucznia.</p>
                <button
                  className="gold-button"
                  onClick={addModule}
                  data-testid="empty-add-module-button"
                >
                  Dodaj pierwszy moduł
                </button>
              </div>
            ) : (
              <div className="lesson-form" data-testid="lesson-editor-form">
                <div className="form-heading">
                  <span>EDYCJA LEKCJI</span>
                  <h1>{lesson.title}</h1>
                </div>
                <section className="glass-form-section">
                  <h3>Podstawowe informacje</h3>
                  <label>
                    TYTUŁ
                    <input
                      value={lesson.title}
                      onChange={(e) => updateLesson("title", e.target.value)}
                      data-testid="lesson-title-input"
                    />
                  </label>
                  <div className="form-grid">
                    <label>
                      WŁASNY NUMER / ETYKIETA
                      <input
                        value={lesson.number_label || ""}
                        onChange={(e) =>
                          updateLesson("number_label", e.target.value)
                        }
                        placeholder="Automatycznie, np. 1.1"
                        data-testid="lesson-number-input"
                      />
                    </label>
                    <label>
                      CZAS TRWANIA — AUTOMATYCZNIE
                      <input
                        value={
                          readingDuration
                            ? "Odczytuję czas…"
                            : lesson.duration || "Dodaj wideo"
                        }
                        readOnly
                        data-testid="lesson-duration-input"
                      />
                    </label>
                  </div>
                  <label>
                    PLAKAT WIDEO
                    <input
                      value={lesson.video_poster || ""}
                      onChange={(e) =>
                        updateLesson("video_poster", e.target.value)
                      }
                      placeholder="URL obrazu"
                      data-testid="video-poster-input"
                    />
                  </label>
                  <label>
                    KRÓTKIE WPROWADZENIE
                    <textarea
                      value={lesson.content}
                      onChange={(e) => updateLesson("content", e.target.value)}
                      placeholder="Każdy nowy wiersz zostanie zachowany u ucznia"
                      data-testid="lesson-content-input"
                    />
                  </label>
                </section>
                <LessonAdvancedSettings
                  course={course}
                  lesson={lesson}
                  updateLesson={updateLesson}
                />
                <LessonBlockEditor
                  blocks={lesson.content_blocks || []}
                  onChange={(value) => updateLesson("content_blocks", value)}
                />
                <LessonCustomFormEditor
                  value={lesson.custom_form}
                  onChange={(value) => updateLesson("custom_form", value)}
                  bots={bots}
                />
                <section className="glass-form-section">
                  <div className="section-inline">
                    <div>
                      <h3>Materiał wideo</h3>
                      <p>
                        Po wklejeniu linku czas lekcji i łączny czas kursu
                        uzupełnią się automatycznie.
                      </p>
                    </div>
                    <select
                      value={lesson.video_type}
                      onChange={(e) =>
                        updateLesson("video_type", e.target.value)
                      }
                      data-testid="video-type-select"
                    >
                      <option value="youtube">YouTube</option>
                      <option value="external">Vimeo / Bunny</option>
                      <option value="upload">Власний файл</option>
                    </select>
                  </div>
                  <label>
                    <Link2 /> ADRES WIDEO
                    <input
                      value={lesson.video_url}
                      onChange={(e) => updateVideoUrl(e.target.value)}
                      placeholder="Wklej link YouTube або Vimeo"
                      data-testid="video-url-input"
                    />
                  </label>
                  {readingDuration && (
                    <p className="duration-reading">
                      Odczytuję długość materiału…
                    </p>
                  )}
                  <LargeVideoUploader onReady={videoReady} />
                </section>
                <section className="glass-form-section">
                  <div className="section-inline">
                    <div>
                      <h3>Zadanie i dostęp</h3>
                      <p>Ustal warunek otwarcia następnej lekcji.</p>
                    </div>
                    <label className="switch-row">
                      AKTYWNE
                      <input
                        type="checkbox"
                        checked={lesson.homework_enabled}
                        onChange={(e) =>
                          updateLesson("homework_enabled", e.target.checked)
                        }
                        data-testid="homework-enabled-checkbox"
                      />
                    </label>
                  </div>
                  {course.homework_enabled !== false &&
                    lesson.homework_enabled && (
                      <>
                        <div className="form-grid">
                          <label>
                            TYP ZADANIA
                            <select
                              value={lesson.assignment_type || "text"}
                              onChange={(e) =>
                                updateLesson("assignment_type", e.target.value)
                              }
                              data-testid="assignment-type-select"
                            >
                              <option value="text">Odpowiedź tekstowa</option>
                              <option value="open">Otwarte pytanie</option>
                              <option value="link">Link do pracy</option>
                              <option value="file">Przesłanie pliku</option>
                              <option value="quiz">Test automatyczny</option>
                            </select>
                          </label>
                          <label>
                            OTWÓRZ KOLEJNĄ LEKCJĘ
                            <select
                              value={lesson.unlock_rule || "lesson_complete"}
                              onChange={(e) =>
                                updateLesson("unlock_rule", e.target.value)
                              }
                              data-testid="unlock-rule-select"
                            >
                              <option value="immediate">Od razu</option>
                              <option value="lesson_complete">
                                Po ukończeniu lekcji
                              </option>
                              <option value="homework_submitted">
                                Po wysłaniu zadania
                              </option>
                              <option value="curator_approved">
                                Po akceptacji kuratora
                              </option>
                              <option value="test_passed">
                                Po zdaniu testu
                              </option>
                            </select>
                          </label>
                        </div>
                        {lesson.assignment_type === "quiz" && (
                          <div
                            className="quiz-builder"
                            data-testid="quiz-builder"
                          >
                            <label>
                              PRÓG ZALICZENIA
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={lesson.passing_score || 70}
                                onChange={(e) =>
                                  updateLesson(
                                    "passing_score",
                                    Number(e.target.value),
                                  )
                                }
                                data-testid="passing-score-input"
                              />
                            </label>
                            {(lesson.quiz_questions || []).map((q, qi) => (
                              <article key={q.id}>
                                <header>
                                  <b>Pytanie {qi + 1}</b>
                                  <button
                                    onClick={() =>
                                      updateLesson(
                                        "quiz_questions",
                                        lesson.quiz_questions.filter(
                                          (x) => x.id !== q.id,
                                        ),
                                      )
                                    }
                                    data-testid={`delete-question-${q.id}`}
                                  >
                                    <Trash2 />
                                  </button>
                                </header>
                                <input
                                  value={q.question}
                                  onChange={(e) =>
                                    updateQuestion(q.id, {
                                      question: e.target.value,
                                    })
                                  }
                                  data-testid={`question-text-${q.id}`}
                                />
                                {q.options.map((option, oi) => (
                                  <div className="option-edit" key={oi}>
                                    <input
                                      type="radio"
                                      checked={q.correct_index === oi}
                                      onChange={() =>
                                        updateQuestion(q.id, {
                                          correct_index: oi,
                                        })
                                      }
                                      data-testid={`correct-option-${q.id}-${oi}`}
                                    />
                                    <input
                                      value={option}
                                      onChange={(e) => {
                                        const options = [...q.options];
                                        options[oi] = e.target.value;
                                        updateQuestion(q.id, { options });
                                      }}
                                      data-testid={`option-text-${q.id}-${oi}`}
                                    />
                                  </div>
                                ))}
                              </article>
                            ))}
                            <button
                              className="ghost-add"
                              onClick={addQuestion}
                              data-testid="add-quiz-question-button"
                            >
                              <Plus /> Dodaj pytanie
                            </button>
                          </div>
                        )}
                      </>
                    )}
                </section>
                <section className="glass-form-section">
                  <div className="section-inline">
                    <div>
                      <h3>Kastomowe przyciski</h3>
                      <p>Dodaj CTA do materiałów, społeczności lub oferty.</p>
                    </div>
                    <button
                      className="ghost-add"
                      onClick={addButton}
                      data-testid="add-custom-button"
                    >
                      <Plus /> Dodaj
                    </button>
                  </div>
                  <div className="cta-builder">
                    {(lesson.custom_buttons || []).map((button, buttonIndex) => (
                      <article key={button.id}>
                        <div className="cta-order-actions"><button onClick={() => moveButton(button.id, -1)} disabled={buttonIndex === 0} aria-label="Przenieś przycisk wyżej"><ArrowUp/></button><button onClick={() => moveButton(button.id, 1)} disabled={buttonIndex === lesson.custom_buttons.length - 1} aria-label="Przenieś przycisk niżej"><ArrowDown/></button></div>
                        <input value={button.label} onChange={(e) => updateButton(button.id, { label: e.target.value })} placeholder="Nazwa" data-testid={`custom-button-label-${button.id}`}/>
                        <input value={button.url} onChange={(e) => updateButton(button.id, { url: e.target.value })} placeholder="https://" data-testid={`custom-button-url-${button.id}`}/>
                        <select value={button.style || "gold"} onChange={(e) => updateButton(button.id, { style: e.target.value })} data-testid={`custom-button-style-${button.id}`}>
                          <option value="gold">Złoty</option><option value="glass">Szklany</option><option value="outline">Kontur</option><option value="spotlight">Spotlight</option><option value="whatsapp">WhatsApp</option><option value="telegram">Telegram</option><option value="danger">Czerwony</option>
                        </select>
                        <select value={button.icon || "external"} onChange={(e) => updateButton(button.id, { icon: e.target.value })} data-testid={`custom-button-icon-${button.id}`}>
                          <option value="none">Bez ikony</option><option value="external">Strzałka</option><option value="whatsapp">WhatsApp</option><option value="telegram">Telegram</option><option value="download">Pobieranie</option><option value="calendar">Kalendarz</option><option value="play">Play</option>
                        </select>
                        <select value={button.size || "medium"} onChange={(e) => updateButton(button.id, { size: e.target.value })} data-testid={`custom-button-size-${button.id}`}>
                          <option value="small">Mały</option><option value="medium">Średni</option><option value="large">Duży</option><option value="full">Pełna szerokość</option>
                        </select>
                        <label className="cta-tab-toggle">
                          NOWA KARTA
                          <input type="checkbox" checked={button.new_tab !== false} onChange={(e) => updateButton(button.id, { new_tab: e.target.checked })} data-testid={`custom-button-new-tab-${button.id}`}/>
                        </label>
                        <LessonVisibilityEditor value={button.appearance} onChange={(appearance) => updateButton(button.id, { appearance })} compact/>
                        <button onClick={() => updateLesson("custom_buttons", lesson.custom_buttons.filter((item) => item.id !== button.id))} data-testid={`delete-custom-button-${button.id}`} aria-label="Usuń przycisk"><Trash2 /></button>
                      </article>
                    ))}
                  </div>
                </section>
              </div>
            )}
          </main>
        </div>
      )}
      {step === "extras" && (
        <div className="course-extras-step" data-testid="course-extras-step">
          <CourseExtrasEditor course={course} setCourse={setCourse} />
          <CertificateAdvancedEditor course={course} setCourse={setCourse} />
        </div>
      )}
    </div>
  );
}
